// Marks an optional attribute the guest explicitly declined, as opposed to one not yet reached.
export const SKIPPED = null;

export function getProductAttributes(product) {
    if (!Array.isArray(product?.attributes)) {
        return [];
    }

    // The API already orders attributes by queue, so the incoming order is preserved as-is.
    return product.attributes
        .map((attribute) => ({
            attribute_id: attribute.attribute_id ?? attribute.id ?? null,
            name: attribute.name,
            required: Boolean(attribute.required),
            queue: Number(attribute.queue ?? 0),
            values: (Array.isArray(attribute.values) ? attribute.values : []).map((value) => ({
                id: value.id,
                value: value.value,
                price: Number(value.price ?? 0),
                duration: Number(value.duration ?? 0),
                incompatible_with_ids: Array.isArray(value.incompatible_with_ids) ? value.incompatible_with_ids : [],
            })),
        }))
        .filter((attribute) => Boolean(attribute.name) && attribute.values.length > 0);
}

// Maps each attribute name to a Map of blocked value ID -> names of the selections that block it.
export function buildIncompatibilityMap(attributes = [], selections = {}) {
    const activeSelections = attributes
        .map((attribute) => ({ attribute, value: findSelectedValue(attribute, selections) }))
        .filter((entry) => Boolean(entry.value));

    const blockedByAttribute = {};

    attributes.forEach((attribute) => {
        const blocked = new Map();

        activeSelections.forEach(({ attribute: source, value }) => {
            // A single-select attribute can never conflict with its own values.
            if (source.name === attribute.name) {
                return;
            }

            value.incompatible_with_ids.forEach((blockedID) => {
                if (!attribute.values.some((candidate) => candidate.id === blockedID)) {
                    return;
                }

                const reasons = blocked.get(blockedID) ?? [];

                if (!reasons.includes(value.value)) {
                    reasons.push(value.value);
                }

                blocked.set(blockedID, reasons);
            });
        });

        blockedByAttribute[attribute.name] = blocked;
    });

    return blockedByAttribute;
}

export function isAttributeBlocked(attribute, blockedValues) {
    return Boolean(attribute?.values?.length)
        && attribute.values.every((value) => blockedValues?.has(value.id));
}

export function getServiceAttributes(service = {}) {
    if (Array.isArray(service.selected_attributes) && service.selected_attributes.length > 0) {
        return service.selected_attributes.map((attribute) => ({
            name: attribute.attribute_name,
            value: attribute.attribute_value,
            attribute_id: attribute.attribute_id,
            attribute_value_id: attribute.attribute_value_id,
        }));
    }

    return Object.entries(service.attribute_selection ?? {}).map(([name, selection]) => ({
        name,
        value: selection?.value ?? selection,
        attribute_id: selection?.attribute_id,
        attribute_value_id: selection?.attribute_value_id,
    }));
}

// Bookings made before multi-service carry the service fields on the booking itself.
export function normalizeBookingServices(booking = {}) {
    const services = Array.isArray(booking.selected_services) && booking.selected_services.length > 0
        ? booking.selected_services
        : [booking];

    return services
        .map((service) => ({
            product_id: service.product_id ?? service.product?.id ?? null,
            product_name: service.product_name ?? service.product?.name ?? '',
            total_price: Number(service.total_price ?? 0),
            total_duration: Number(service.total_duration ?? 0),
            attribute_selection: service.attribute_selection ?? {},
            attributes: getServiceAttributes(service),
        }))
        .filter((service) => Boolean(service.product_id));
}

export function findSelectedValue(attribute, selections) {
    const selectedValueID = selections?.[attribute.name];

    if (selectedValueID === undefined || selectedValueID === SKIPPED) {
        return null;
    }

    return attribute.values.find((value) => value.id === selectedValueID) ?? null;
}

export function isAnswered(attribute, selections) {
    return Object.prototype.hasOwnProperty.call(selections ?? {}, attribute.name);
}

export function getSelectedServiceDetails(product, selections = {}) {
    if (!product) {
        return null;
    }

    const basePrice = Number(product.base_price ?? 0);
    const baseDuration = Number(product.base_duration ?? 0);
    const selectedAttributePayload = [];
    const attributeSelection = {};

    let totalPrice = basePrice;
    let totalDuration = baseDuration;

    getProductAttributes(product).forEach((attribute) => {
        const value = findSelectedValue(attribute, selections);

        if (!value) {
            return;
        }

        const selectedAttribute = {
            attribute_name: attribute.name,
            attribute_id: attribute.attribute_id,
            attribute_value: value.value,
            attribute_value_id: value.id,
            duration: value.duration,
            price: value.price,
        };

        selectedAttributePayload.push(selectedAttribute);
        attributeSelection[attribute.name] = {
            attribute_id: attribute.attribute_id,
            attribute_value_id: value.id,
            value: value.value,
        };
        totalPrice += value.price;
        totalDuration += value.duration;
    });

    return {
        product: {
            id: product.id,
            name: product.name,
            description: product.description,
        },
        selected_attributes: selectedAttributePayload,
        attribute_selection: attributeSelection,
        product_id: product.id,
        product_name: product.name,
        product_description: product.description,
        base_price: basePrice,
        base_duration: baseDuration,
        total_price: totalPrice,
        total_duration: totalDuration,
        attributes: selectedAttributePayload,
        productDuration: {
            duration: totalDuration,
            price: totalPrice,
        },
    };
}
